import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "./ui/Button";
import { PendingTournamentBanner } from "./PendingTournamentBanner";
import { ThemeToggle } from "./ThemeToggle";
import { ROUTES, APP_NAME } from "@/utils/constants";
import { clsx } from "clsx";

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: ROUTES.TOURNAMENTS, label: "Tournaments" },
    { path: ROUTES.MY_LINEUPS, label: "My Lineups" },
    { path: ROUTES.CAREER_LEADERBOARD, label: "Career Stats" },
    { path: ROUTES.PROFILE, label: "Profile" },
  ];

  const adminLinks =
    user?.role === "admin"
      ? [{ path: ROUTES.ADMIN_DASHBOARD, label: "Admin" }]
      : [];

  return (
    <div className="min-h-screen bg-cream-100 dark:bg-charcoal-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-charcoal-800 shadow-sm sticky top-0 z-40">
        <nav className="container-custom">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to={ROUTES.HOME} className="flex items-center space-x-2">
              <span className="font-display text-2xl font-bold text-forest-700 dark:text-forest-300">
                {APP_NAME}
              </span>
            </Link>

            {/* Desktop Navigation */}
            {isAuthenticated && (
              <>
                <div className="hidden md:flex items-center space-x-1">
                  {[...navLinks, ...adminLinks].map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={clsx(
                        "px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors",
                        isActive(link.path)
                          ? "bg-forest-50 text-forest-700 dark:bg-forest-900 dark:text-forest-300"
                          : "text-charcoal-700 hover:bg-cream-200 dark:text-cream-200 dark:hover:bg-charcoal-700",
                      )}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>

                <div className="hidden md:flex items-center space-x-4">
                  <ThemeToggle />
                  <span className="text-sm text-charcoal-600 dark:text-charcoal-300 font-sans">
                    {user?.displayName}
                  </span>
                  <Button onClick={handleLogout} variant="ghost" size="sm">
                    Logout
                  </Button>
                </div>
              </>
            )}

            {!isAuthenticated && (
              <div className="hidden md:flex items-center space-x-4">
                <ThemeToggle />
                <Button
                  onClick={() => navigate(ROUTES.LOGIN)}
                  variant="ghost"
                  size="sm"
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate(ROUTES.SIGNUP)}
                  variant="primary"
                  size="sm"
                >
                  Sign Up
                </Button>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-cream-200 dark:hover:bg-charcoal-700 focus:outline-none focus-ring"
              aria-label="Toggle menu"
            >
              <svg
                className="h-6 w-6 text-charcoal-700 dark:text-cream-200"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && isAuthenticated && (
            <div className="md:hidden py-4 border-t border-charcoal-200 dark:border-charcoal-700 animate-slide-up">
              <div className="flex flex-col space-y-2">
                {[...navLinks, ...adminLinks].map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={clsx(
                      "px-4 py-2 rounded-lg font-sans text-sm font-medium",
                      isActive(link.path)
                        ? "bg-forest-50 text-forest-700 dark:bg-forest-900 dark:text-forest-300"
                        : "text-charcoal-700 hover:bg-cream-200 dark:text-cream-200 dark:hover:bg-charcoal-700",
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 border-t border-charcoal-200 dark:border-charcoal-700">
                  <div className="px-4 py-2">
                    <ThemeToggle />
                  </div>
                  <p className="px-4 py-2 text-sm text-charcoal-600 dark:text-charcoal-300 font-sans">
                    {user?.displayName}
                  </p>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-charcoal-700 hover:bg-cream-200 dark:text-cream-200 dark:hover:bg-charcoal-700"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Pending Tournament Banner */}
      {isAuthenticated && <PendingTournamentBanner />}

      {/* Main content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-white dark:bg-charcoal-800 border-t border-charcoal-200 dark:border-charcoal-700 mt-12">
        <div className="container-custom py-6">
          <p className="text-center text-sm text-charcoal-600 dark:text-charcoal-300 font-sans">
            {APP_NAME} • Since 2014
          </p>
        </div>
      </footer>
    </div>
  );
};
